import { Component, OnInit, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import { UserContextService } from '../../services/user-context.service';
import { SidebarVisibilityService } from '../../services/sidebar-visibility.service';
import { AuthNavigationService } from '../../services/auth-navigation.service';
import { AuthRoutes } from '../../constants/auth.constants';
import { RedirectingToLoginError } from '../../services/auth.service';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css'],
  standalone: false
})
export class HomeComponent implements OnInit, AfterViewInit {
  isInitializing = true;
  isRedirectingToLogin = false;
  showError = false;
  redirectTo: string | null = null;
  hasNavigated = false;

  constructor(
    private userContext: UserContextService,
    private sidebarVisibilityService: SidebarVisibilityService,
    private router: Router,
    private authNav: AuthNavigationService
  ) {}

  async ngOnInit(): Promise<void> {
    try {
      if (!this.sidebarVisibilityService || !this.userContext) {
        this.showError = true;
        this.isInitializing = false;
        return;
      }

      // Only initialize once per session
      if (this.sidebarVisibilityService.homeInitialized) {
        this.isInitializing = false;
        
        if (!this.userContext.sourceId && !this.userContext.shouldShowTenantSelection) {
          try {
            await this.userContext.initializeAsync();
          } catch (ex) {
            if (ex instanceof RedirectingToLoginError) {
              this.isRedirectingToLogin = true;
              return;
            }
          }
        }
        
        this.determineRedirect();
        return;
      }

      // Hide sidebar during authentication
      this.sidebarVisibilityService.showSidebar = false;

      // Authenticate and get user context
      try {
        await this.userContext.initializeAsync();
        
        if (!this.userContext.hasAccess()) {
          this.authNav.navigateToNotAuthorizedPermissions();
          return;
        }
      } catch (err) {
        if (err instanceof RedirectingToLoginError) {
          this.isRedirectingToLogin = true;
          return;
        }
      }

      // Mark as initialized
      this.sidebarVisibilityService.homeInitialized = true;
      this.isInitializing = false;
      this.showError = false;
      
      this.determineRedirect();
    } catch (err) {
      if (err instanceof RedirectingToLoginError) {
        this.isRedirectingToLogin = true;
        return;
      }
      this.isInitializing = false;
      this.showError = false;
      if (this.sidebarVisibilityService) {
        this.sidebarVisibilityService.homeInitialized = true;
        this.sidebarVisibilityService.showSidebar = true;
      }
      this.determineRedirect();
    }
  }

  ngAfterViewInit(): void {
    // Navigate after view is initialized
    setTimeout(() => {
      if (this.isRedirectingToLogin) {
        return;
      }
      if (!this.hasNavigated && !this.showError && this.redirectTo) {
        const currentUrl = this.router.url;
        const isOnHomePage = currentUrl === AuthRoutes.home || currentUrl === '/Home';
        
        if (isOnHomePage) {
          this.hasNavigated = true;
          setTimeout(() => {
            this.router.navigate([this.redirectTo!]);
          }, 100);
        }
      } else if (!this.hasNavigated && !this.redirectTo && !this.showError) {
        this.determineRedirect();
      }
    }, 100);
  }

  private determineRedirect(): void {
    if (!this.userContext) {
      this.redirectTo = AuthRoutes.flaggedContacts;
      return;
    }

    if (!this.userContext.hasAccess()) {
      this.authNav.navigateToNotAuthorizedPermissions();
      return;
    }

    if (this.userContext.hasNoTenants) {
      this.authNav.navigateToNotAuthorizedNoTenants();
      return;
    }

    if (this.userContext.shouldShowTenantSelection) {
      this.redirectTo = AuthRoutes.associationSelection;
    } else if (this.userContext.sourceId) {
      this.redirectTo = AuthRoutes.flaggedContacts;
      if (this.sidebarVisibilityService) {
        this.sidebarVisibilityService.showSidebar = true;
      }
    } else {
      this.authNav.navigateToNotAuthorizedNoTenants();
    }
  }

  get userContextSourceId(): string | null {
    return this.userContext?.sourceId || null;
  }

  get userContextIsAgVantisUser(): boolean {
    return this.userContext?.isAgVantisUser ?? false;
  }

  get userContextShouldShowTenantSelection(): boolean {
    return this.userContext?.shouldShowTenantSelection ?? false;
  }
}

